/**
 * Opening the Workspace is one arrival: each time the wall shows, the frost
 * comes at once (`WallFrost` follows the show) and the wall stays
 * transparent over it until what it shows is in place (the first page,
 * Pinned, the new cards; the masonry measures its cards before it paints),
 * then fades in whole. A short cap keeps a slow load from holding it: the
 * wall then shows what it has, and the rest fills in.
 */
import { useEffect, useState } from "react";

/** The longest the wall waits for its content before it shows anyway. */
export const WALL_REVEAL_CAP_MS = 350;

/**
 * Whether the shown wall is revealed. `ready`: what it shows is in place.
 * The reveal comes one frame after `ready`, so the commit that made it ready
 * (Pinned's live frames, the measured cards) is drawn, still transparent,
 * before the fade starts.
 */
export function useWallReveal(visible: boolean, ready: boolean): boolean {
  const [revealed, setRevealed] = useState(false);
  // Hidden, the wall waits again for its next show.
  if (!visible && revealed) setRevealed(false);
  const waiting = visible && !revealed;

  useEffect(() => {
    if (!waiting) return;
    const timer = window.setTimeout(
      () => setRevealed(true),
      WALL_REVEAL_CAP_MS,
    );
    return () => window.clearTimeout(timer);
  }, [waiting]);

  useEffect(() => {
    if (!waiting || !ready) return;
    const frame = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(frame);
  }, [ready, waiting]);

  return visible && revealed;
}
